import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

import { InfoPage } from '@/components/info-page.js';
import { TableErrorState } from '@/components/table/table-error-state.js';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown.js';
import { TableFilterInputField } from '@/components/table/table-filter-field.js';
import { TableFilterPlainDateRangePicker } from '@/components/table/table-filter-plain-date-range-picker.js';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button.js';
import { Button } from '@/components/ui/button.js';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu.js';
import { useOrgOptions, useSchoolOptions } from '@/lib/api-call-hooks.js';
import { EmptyArray, getAdminSessions } from '@/lib/api-calls.js';
import { useRowsBulkSelection } from '@/lib/bulk-selection.js';
import { useErrorToast } from '@/lib/error-handling.js';
import { usePaginatedFilters } from '@/lib/filtering.js';
import { paginationRequestSchema } from '@/lib/pagination.js';
import { pluralize } from '@/lib/string-utils.js';
import { CancelSessions } from '@/routes/_layout/sessions/-components/cancel-sessions.js';
import { CreateSessionModal } from '@/routes/_layout/sessions/-components/create-session-modal.js';
import { RestoreSessions } from '@/routes/_layout/sessions/-components/restore-sessions.js';
import { SessionsTable } from '@/routes/_layout/sessions/-components/sessions-table.js';
import { sessionsTabs } from '@/routes/_layout/sessions/-components/tabs.js';
import { FrequentTimeZones, isAirAdmin, plainDateFromStringOrElseUndefined } from '@airreading/common';
import { AdminSession } from '@airreading/common/data';
import { BulkRefreshZoomLinks } from '../-components/bulk-refresh-zoom-links.js';
import { validateRouteRole } from '@/lib/routing.js';

export const Route = createFileRoute('/_layout/sessions/sub-sessions/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: SubSessionsIndex,
  validateSearch: paginationRequestSchema.extend({
    query: z.string().optional(),
    orgs: z.string().array().optional(),
    schools: z.string().array().optional(),
    lessons: z.string().array().optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
  }),
});

const nowInCST = Temporal.Now.zonedDateTimeISO(FrequentTimeZones.UsCentral);
const startOfTodayInstantInCST = nowInCST.startOfDay().toInstant();

function SubSessionsIndex() {
  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[query, activeQuery], setQuery],
      orgs: [[selectedOrgs, activeSelectedOrgs], setSelectedOrgs],
      schools: [[selectedSchools, activeSelectedSchools], setSelectedSchools],
      startDate: [[selectedStartDate], setSelectedStartDate],
      endDate: [[selectedEndDate], setSelectedEndDate],
    },
    resetFilters,
  } = usePaginatedFilters(Route.id, ['query', 'orgs', 'schools', 'startDate', 'endDate']);

  const endPlanDate = plainDateFromStringOrElseUndefined(selectedEndDate);

  const {
    data: sessionsResp,
    isPending,
    error,
    refetch,
  } = useQuery(
    getAdminSessions({
      pageIndex,
      pageSize,
      filters: {
        startOnOrAfter: selectedStartDate
          ? Temporal.PlainDate.from(selectedStartDate)
              .toZonedDateTime(FrequentTimeZones.UsCentral)
              .startOfDay()
              .toInstant()
              .toString()
          : startOfTodayInstantInCST.toString(),
        startOnOrBefore: endPlanDate
          ? endPlanDate
              .toZonedDateTime(FrequentTimeZones.UsCentral)
              .add({ days: 1 })
              .startOfDay()
              .subtract({ milliseconds: 1 })
              .toInstant()
              .toString()
          : undefined,
        hasOpenSubRequest: true,
        query: activeQuery,
        orgs: activeSelectedOrgs,
        schools: activeSelectedSchools,
      },
      sortBy: ['start-asc'],
    }),
  );

  useErrorToast(error, 'Error loading sessions. Please try again.');

  const sessions = useMemo(() => sessionsResp?.adminSessions || EmptyArray, [sessionsResp]);
  const rowCount = sessionsResp?.total || 0;
  const allSchoolOptions = useSchoolOptions();
  const allOrgOptions = useOrgOptions();

  const { rowSelection, rowDataSelection, onRowSelectionChange, getRowId, resetAllRowSelection } =
    useRowsBulkSelection<AdminSession>({
      getRowId: (row) => row.id,
      rowIdToData: (id) => sessions.find((session) => session.id === id),
    });

  const bulkActionsMenuItems = (
    <>
      <CancelSessions
        sessions={rowDataSelection}
        trigger={
          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
            {pluralize(rowDataSelection.length, 'Cancel session', 'Cancel sessions')}
          </DropdownMenuItem>
        }
        onSuccess={resetAllRowSelection}
      />
      <RestoreSessions
        sessions={rowDataSelection}
        trigger={
          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
            {pluralize(rowDataSelection.length, 'Restore session', 'Restore sessions')}
          </DropdownMenuItem>
        }
        onSuccess={resetAllRowSelection}
      />
      <BulkRefreshZoomLinks
        sessions={rowDataSelection}
        trigger={
          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
            {pluralize(rowDataSelection.length, 'Refresh zoom host link', 'Refresh zoom host links')}
          </DropdownMenuItem>
        }
        onSuccess={resetAllRowSelection}
      />
    </>
  );

  const filterLeftComponents = useMemo(
    () => (
      <>
        <TableFilterInputField
          className="w-[250px] lg:w-[350px] xl:w-[400px] 2xl:w-[450px]"
          placeholder="Search sessions by student, teacher, email or lesson name"
          value={query ?? ''}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
        />
        <TableFilterPlainDateRangePicker
          fromPlainDateString={selectedStartDate}
          toPlainDateString={selectedEndDate}
          onRangeSelect={(range) => {
            setSelectedStartDate(range.fromPlainDateString);
            setSelectedEndDate(range.toPlainDateString);
          }}
          placeholder="Date"
        />
        <TableFilterDropdown
          title="District"
          options={allOrgOptions}
          selectedValues={selectedOrgs ?? []}
          onChange={setSelectedOrgs}
        />
        <TableFilterDropdown
          title="School"
          options={allSchoolOptions}
          selectedValues={selectedSchools ?? []}
          onChange={setSelectedSchools}
        />
        <TableFilterResetButton show onClick={resetFilters} />
      </>
    ),
    [
      query,
      selectedStartDate,
      selectedEndDate,
      allOrgOptions,
      selectedOrgs,
      setSelectedOrgs,
      allSchoolOptions,
      selectedSchools,
      setSelectedSchools,
      resetFilters,
      setQuery,
      setSelectedStartDate,
      setSelectedEndDate,
    ],
  );

  const topBarRightComponents = <CreateSessionModal trigger={<Button>New session</Button>} />;

  return (
    <InfoPage headerText="Sessions" tabs={sessionsTabs('Sub sessions')}>
      <SessionsTable
        sessions={sessions}
        hasError={!!error}
        isLoading={isPending}
        errorState={<TableErrorState refetchAction={refetch} />}
        tableHookProps={{
          rowCount,
          state: {
            pagination: {
              pageIndex,
              pageSize,
            },
            rowSelection,
          },
          onPaginationChange,
          onRowSelectionChange,
          getRowId,
        }}
        displayTimeZone={FrequentTimeZones.UsCentral}
        filters={{ filterLeftComponents }}
        topBarRightComponents={topBarRightComponents}
        bulkActionsMenuItems={bulkActionsMenuItems}
        from="sub"
      />
    </InfoPage>
  );
}
